import { Ban, Check, X, RotateCw } from "lucide-react";
import Tooltip from '@mui/material/Tooltip';

export function parseUSAddress(full_address) {
    if (!full_address || typeof full_address !== "string") {
        return null;
    }

    const result = {
        house: null,
        street: null,
        address: null,
        city: null,
        state: null,
        zip: null
    };

    const parts = full_address.split(",").map(p => p.trim());

    // ✅ Extract basic parts safely
    if (parts.length >= 1) result.address = parts[0] || null;
    if (parts.length >= 2) result.city = parts[1] || null;

    if (parts.length >= 3) {
        const stateZipPart = parts.slice(2).join(" ");

        const stateZipMatch = stateZipPart.match(/([A-Z]{2})\s*,?\s*(\d{5})/i);
        if (stateZipMatch) {
            result.state = stateZipMatch[1].toUpperCase();
            result.zip = stateZipMatch[2];
        }
    }

    // ✅ Extract house + street (optional, don't break if fails)
    if (result.address) {
        const addrMatch = result.address.match(/^(\d+)\s+(.*)$/);

        if (addrMatch) {
            result.house = addrMatch[1];
            result.street = addrMatch[2] || null;
        } else {
            // No house number, treat full as street
            result.street = result.address;
        }
    }

    return result;
}

export const hasPermission = (permName, user) => {
    if (!permName) return false;
    return user?.permissions?.some((p) => p.permission_name === permName && p.can_view === 1);
};

export const hasNotPermission = (permName) => {
    if (!permName) return false;
    return !hasPermission(permName);
};

export const filterLinks = (links, user) =>
    links.filter((link) => {
        if (link.permission) return hasPermission(link.permission, user);
        if (link.notPermission) return hasNotPermission(link.notPermission);
        return true;
    });

export const getAlignClass = (align) => {
    switch (align) {
        case "center":
            return "!text-center";
        case "right":
            return "!text-right";
        default:
            return "!text-left";
    }
};


export const formatTime = (iso, timezone = "America/New_York") => {
    if (!iso) return "";
    const date = new Date(iso);
    if (isNaN(date.getTime())) return iso;
    return date.toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        timeZone: timezone,
        hour12: true,
        // timeZoneName: "short", // adds "EDT" / "EST"
    });
};

export const toDateOrNull = (value) => {
    if (!value) return null;
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
};


const colors = ["#2563eb", "#16a34a", "#d97706", "#dc2626", "#9333ea"];
export const getOwnerColor = (idx) => colors[idx % colors.length];

export const statuses = {
    pending: "Pending",
    lead_added: "Lead Added",
    lead_fetched: "Lead fetched",
    fastpeople_done: "FastPeople search completed",
    edq_done: "EDQ validation done",
    ai_done: "AI call finished",
    contact_saved: "Contact saved to GHL",
    contact_saved_kixie: "Contact saved to Kixie",
};

const marketTierClasses = {
    "Super Hot": "bg-red-100 text-red-700",
    Hot: "bg-orange-100 text-orange-700",
    Warm: "bg-yellow-100 text-yellow-700",
    Neutral: "bg-blue-100 text-blue-700",
    Cool: "bg-cyan-100 text-cyan-700",
    Cold: "bg-blue-50 text-blue-600",
    "No Activity": "bg-gray-100 text-gray-500",
};

const getMarketTierClass = (tier) => marketTierClasses[tier] || "bg-gray-100 text-gray-500";

export const getMetricScoreClass = (score) => {
    if (score === 2) return "text-green-600 font-semibold";
    if (score === 1) return "text-yellow-600 font-semibold";
    if (score === 0) return "text-red-600 font-semibold";
    return "text-gray-400";
};

const getMetricScoreLabel = (score) => {
    if (score === 2) return "Seller";
    if (score === 1) return "Neutral";
    if (score === 0) return "Buyer";
    return "N/A";
};

const buildMarketScoreTooltip = (market) => (
    <div>
        <div className="text-center"><b>Market Score</b></div>
        <div><b>Total:</b> {market?.score ?? "N/A"} / 100</div>
        <div><b>Tier:</b> {market?.tier || "N/A"}</div>
        <div className="mt-2"><b>Weighted Metrics</b></div>
        {(market?.breakdown || []).map((item) => (
            <div key={item.key} className="grid grid-cols-2 gap-x-2">
                <span>{item.label}:</span>
                <span>
                    {item.temperature || getMetricScoreLabel(item.rawScore)}
                    {item.value !== null && ` (${item.points}/${item.maxPoints || item.weight} pts)`}
                </span>
            </div>
        ))}
    </div>
);

const getTemperatureClass = (temp) => {
    const map = {
        'Super Hot': 'bg-red-100 text-red-700',
        'Hot': 'bg-orange-100 text-orange-700',
        'Warm': 'bg-yellow-100 text-yellow-700',
        'Neutral': 'bg-blue-100 text-blue-700',
        'Cool': 'bg-cyan-100 text-cyan-700',
        'Cold': 'bg-blue-50 text-blue-600',
        'No Activity': 'bg-gray-100 text-gray-500',
    };
    return map[temp] || 'bg-gray-50 text-gray-400';
};

const renderMetricWithTemp = (value, temperature, points, tooltipTitle, formula, values) => {
    const tempClass = getTemperatureClass(temperature);
    return (
        <Tooltip
            title={
                <div>
                    <div className="font-bold">{tooltipTitle}</div>
                    <div><b>Formula:</b> {formula}</div>
                    {values && Object.entries(values).map(([k, v]) => (
                        <div key={k}><b>{k}:</b> {v ?? 'N/A'}</div>
                    ))}
                    <div><b>Temperature:</b> {temperature || 'N/A'}</div>
                    <div><b>Points:</b> {points ?? 'N/A'}</div>
                </div>
            }
        >
            <span className="inline-flex items-center gap-1 cursor-help">
                {temperature && (
                    <span className={`px-4 py-1 rounded-full text-xs font-semibold ${tempClass}`}>
                        <span>{value ?? 'N/A'}</span>
                    </span>
                )}
            </span>
        </Tooltip>
    );
};

const LoadingSpinner = () => (
    <>
        <div className="h-4 bg-gray-200 rounded w-full"></div>
    </>
);


export const renderMarketStatusCell = (lead, { onReload, isReloading, onReloadSite, reloadingSites = {} } = {}) => {
    if (!lead) return null;

    // Helper: validate numeric prices
    const isValidValue = (v) => {
        if (v === null || v === undefined || v === "" || v === "--" || v === "Not available") return false;
        const cleaned = String(v).replace(/[^0-9.]/g, "");
        const n = Number(cleaned);
        return !isNaN(n) && n > 0;
    };
    const toNum = (v) => Number(String(v).replace(/[^0-9.]/g, ""));
    const formatCurrency = (val) => {
        if (!val || isNaN(val) || val <= 0) return null;
        return "$" + Math.round(val).toLocaleString();
    };

    // 1. Overall Market Status placeholder (computed below from site consensus with lead.zillow_status fallback)

    const getOverallBadge = (status) => {
        if (!status) {
            return (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500 border border-gray-200">
                    Not Checked
                </span>
            );
        }

        const s = status.toLowerCase();
        if (
            s.includes("off market") ||
            s.includes("not listed") ||
            s.includes("not for sale") ||
            s.includes("not active") ||
            s.includes("unlisted") ||
            s.includes("never listed") ||
            s.includes("listing removed")
        ) {
            return (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-slate-100 text-slate-700 border border-slate-300">
                    {status}
                </span>
            );
        }
        if (s.includes("on market") || s.includes("for sale") || s.includes("active") || s.includes("fsbo")) {
            return (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-300">
                    {status}
                </span>
            );
        }
        if (s.includes("pending") || s.includes("under contract") || s.includes("contingent") || s.includes("auction") || s.includes("pre-foreclosure")) {
            return (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-300">
                    {status}
                </span>
            );
        }
        if (s.includes("sold") || s.includes("closed")) {
            return (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-purple-100 text-purple-800 border border-purple-300">
                    {status}
                </span>
            );
        }
        if (s.includes("rent")) {
            return (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-sky-100 text-sky-800 border border-sky-300">
                    {status}
                </span>
            );
        }

        return (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 border border-gray-300">
                {status}
            </span>
        );
    };

    // 2. Map existing source records from lead.values
    const siteMap = new Map();
    if (Array.isArray(lead.values)) {
        for (const v of lead.values) {
            if (!v || !v.site) continue;
            const siteKey = String(v.site).toLowerCase().trim();
            const existing = siteMap.get(siteKey);
            if (!existing) {
                siteMap.set(siteKey, v);
            } else {
                const existingScore = (existing.url ? 2 : 0) + (existing.market_status ? 2 : 0) + (existing.estimated_value ? 2 : 0);
                const currentScore = (v.url ? 2 : 0) + (v.market_status ? 2 : 0) + (v.estimated_value ? 2 : 0);
                if (currentScore > existingScore || (currentScore === existingScore && (v.id || 0) > (existing.id || 0))) {
                    siteMap.set(siteKey, v);
                }
            }
        }
    }

    // 3. Compute overall market status consensus across all found site records
    const ON_MARKET_KEYWORDS = ["active", "for sale", "listed for sale", "on market", "fsbo"];
    const OFF_MARKET_KEYWORDS = [
        "not listed for sale",
        "not listed",
        "not for sale",
        "off market",
        "off-market",
        "off_market",
        "offmarket",
        "not active",
        "never listed",
        "unlisted",
        "listing removed",
        "removed listing",
        "closed"
    ];

    const allSiteStatuses = [];
    siteMap.forEach((rec, site) => {
        const raw = (rec?.market_status || "").trim();
        const isNotFound = !rec || rec.status === "property_not_found" || (!rec.url && !raw);
        if (raw && !isNotFound) {
            allSiteStatuses.push({ site, status: raw });
        }
    });

    let computedOverallStatus = "";
    if (allSiteStatuses.length > 0) {
        const anyOnMarket = allSiteStatuses.some((x) => {
            const n = x.status.toLowerCase();
            if (OFF_MARKET_KEYWORDS.some((k) => n.includes(k))) return false;
            return ON_MARKET_KEYWORDS.some((k) => n.includes(k));
        });

        const anyPending = !anyOnMarket && allSiteStatuses.some((x) => {
            const n = x.status.toLowerCase();
            return n.includes("pending") || n.includes("under contract") || n.includes("contingent");
        });

        if (anyOnMarket) {
            computedOverallStatus = "On Market";
        } else if (anyPending) {
            computedOverallStatus = "Pending";
        } else {
            const anySold = allSiteStatuses.some((x) => x.status.toLowerCase().includes("sold"));
            if (anySold) {
                computedOverallStatus = "Sold";
            } else {
                computedOverallStatus = "Off Market";
            }
        }
    }

    // Determine final overall status:
    // If we computed a valid status from the sources ("On Market", "Pending", "Off Market", "Sold"), use it!
    // Otherwise fallback to lead.zillow_status, or default to "Not Checked".
    const overallStatus = (
        computedOverallStatus ||
        (lead.zillow_status && lead.zillow_status.toLowerCase() !== "not checked" ? lead.zillow_status : "") ||
        "Not Checked"
    ).trim();

    // 4. Compute overall market value from all valid site values (average), fallback to lead.average_price_value or lead.estimated_value
    const validSiteValues = [];
    siteMap.forEach((rec) => {
        if (isValidValue(rec?.estimated_value)) {
            validSiteValues.push(toNum(rec.estimated_value));
        }
    });

    let overallMarketValue = null;
    if (validSiteValues.length > 0) {
        overallMarketValue = Math.round(
            validSiteValues.reduce((a, b) => a + b, 0) / validSiteValues.length
        );
    } else if (isValidValue(lead.average_price_value)) {
        overallMarketValue = toNum(lead.average_price_value);
    } else if (isValidValue(lead.estimated_value)) {
        overallMarketValue = toNum(lead.estimated_value);
    }

    // Four core sites always evaluated and displayed (found or not found)
    const CORE_SITES = [
        "zillow",
        "homes",
        // "redfin", 
        // "realtor", 
    ];
    const extraSites = Array.from(siteMap.keys()).filter((k) => !CORE_SITES.includes(k));
    const allSiteKeys = [...CORE_SITES];

    return (
        <div className="whitespace-normal flex flex-col gap-1.5 py-1 min-w-[240px]">
            {/* Header: Overall status + Overall Market Value + Reload Button */}
            <div className="flex flex-col gap-1 pb-1.5 border-b border-gray-200">
                <div className="flex items-center justify-between gap-1">
                    <div className={`flex items-center gap-1.5 flex-wrap ${isReloading ? "opacity-75 animate-pulse" : ""}`}>
                        <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                            Overall:
                        </span>
                        {getOverallBadge(overallStatus)}
                    </div>

                    {onReload && (
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                if (!isReloading) onReload();
                            }}
                            disabled={isReloading}
                            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium border transition-colors cursor-pointer shrink-0 ${isReloading
                                ? "bg-teal-50 text-teal-600 border-teal-300 cursor-wait"
                                : "bg-white text-gray-600 hover:text-teal-700 hover:border-teal-400 hover:bg-teal-50 border-gray-300"
                                }`}
                            title="Reload updated prices and statuses from all sites"
                        >
                            <RotateCw size={12} className={isReloading ? "animate-spin text-teal-600" : ""} />
                            <span>{isReloading ? "Reloading..." : "Reload"}</span>
                        </button>
                    )}
                </div>

                <div className={`flex items-center gap-1.5 flex-wrap ${isReloading ? "opacity-75 animate-pulse" : ""}`}>
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                        Market Value:
                    </span>
                    {overallMarketValue ? (
                        <span className="inline-flex items-center font-bold text-emerald-800 bg-emerald-50 px-1.5 py-0.5 rounded text-xs border border-emerald-300" title={`Average of ${validSiteValues.length || 1} source(s)`}>
                            {formatCurrency(overallMarketValue)}
                        </span>
                    ) : (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-500 border border-gray-200">
                            Not Found
                        </span>
                    )}
                </div>
            </div>

            {/* Per-site status & found market value breakdown */}
            <div className="flex flex-col gap-1 pl-1">
                {allSiteKeys.map((siteKey) => {
                    const v = siteMap.get(siteKey);
                    const siteName = siteKey.charAt(0).toUpperCase() + siteKey.slice(1);
                    const url = v?.url;
                    const rawStatus = (v?.market_status || "").trim();
                    const isNotFound = !v || v.status === "property_not_found" || (!url && !rawStatus);
                    const isSiteReloading = Boolean(reloadingSites[siteKey]);
                    const canReloadSite = typeof onReloadSite === "function";

                    const siteRawVal = v?.estimated_value;
                    const sitePrice = isValidValue(siteRawVal) ? toNum(siteRawVal) : null;

                    if (isNotFound) {
                        return (
                            <div key={siteKey} className={`text-xs flex items-center justify-between gap-2 py-0.5 rounded px-1 -mx-1 transition-colors ${isSiteReloading ? "bg-teal-50/70" : ""
                                }`}>
                                <span className="font-medium text-gray-500 text-xs whitespace-nowrap">
                                    [{siteName}]
                                </span>
                                <div className="flex items-center gap-1 shrink-0">
                                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium text-rose-600 bg-rose-50 border border-rose-200 ${isSiteReloading ? "opacity-75 animate-pulse" : ""
                                        }`}>
                                        Not Found
                                    </span>
                                    {canReloadSite && (
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (!isSiteReloading && !isReloading) onReloadSite(siteKey);
                                            }}
                                            disabled={isSiteReloading || isReloading}
                                            className={`inline-flex items-center justify-center p-0.5 rounded border transition-colors cursor-pointer ${isSiteReloading
                                                ? "bg-teal-50 text-teal-600 border-teal-300 cursor-wait"
                                                : "bg-white text-gray-400 hover:text-teal-700 hover:border-teal-400 hover:bg-teal-50 border-gray-200"
                                                }`}
                                            title={`Reload ${siteName} price & status`}
                                        >
                                            <RotateCw size={10} className={isSiteReloading ? "animate-spin text-teal-600" : ""} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    }

                    const stLower = rawStatus.toLowerCase().trim();
                    const isOffMarket =
                        stLower.includes("off market") ||
                        stLower.includes("off-market") ||
                        stLower.includes("off_market") ||
                        stLower.includes("not listed") ||
                        stLower.includes("not for sale") ||
                        stLower.includes("not active") ||
                        stLower.includes("unlisted") ||
                        stLower.includes("never listed") ||
                        stLower.includes("listing removed") ||
                        stLower.includes("removed listing");
                    const isOnMarket = !isOffMarket && (stLower.includes("for sale") || stLower.includes("active") || stLower.includes("on market") || stLower.includes("fsbo"));
                    const isPending = !isOffMarket && !isOnMarket && (stLower.includes("pending") || stLower.includes("under contract") || stLower.includes("contingent"));
                    const isSold = !isOffMarket && !isOnMarket && !isPending && (stLower.includes("sold") || stLower.includes("closed"));

                    const displayStatus = isOffMarket
                        ? "Off Market"
                        : rawStatus
                            ? rawStatus
                            : v.status === "value_missing"
                                ? "No Status"
                                : "Found";

                    const statusBadgeClass = isOnMarket
                        ? "text-emerald-700 bg-emerald-50 border-emerald-300 font-semibold"
                        : isPending
                            ? "text-amber-700 bg-amber-50 border-amber-300 font-semibold"
                            : isSold
                                ? "text-purple-700 bg-purple-50 border-purple-300 font-semibold"
                                : isOffMarket
                                    ? "text-slate-700 bg-slate-50 border-slate-300 font-medium"
                                    : "text-gray-600 bg-gray-50 border-gray-200 font-medium";

                    const priceTooltip = sitePrice
                        ? `Market Value: ${formatCurrency(sitePrice)}`
                        : "";
                    const fullTooltip = [
                        rawStatus && rawStatus.toLowerCase() !== displayStatus.toLowerCase() ? `${displayStatus} (${rawStatus})` : displayStatus,
                        priceTooltip
                    ].filter(Boolean).join(" • ");

                    return (
                        <div key={siteKey} className={`text-xs flex items-center justify-between gap-2 py-0.5 rounded px-1 -mx-1 transition-colors ${isSiteReloading ? "bg-teal-50/70" : ""
                            }`}>
                            <div className="flex items-center gap-1.5 min-w-0">
                                {url ? (
                                    <a
                                        href={url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="font-semibold text-blue-600 hover:text-blue-800 underline text-xs whitespace-nowrap"
                                        title={`View on ${siteName}`}
                                    >
                                        [{siteName}]
                                    </a>
                                ) : (
                                    <span className="font-medium text-gray-600 text-xs whitespace-nowrap">
                                        [{siteName}]
                                    </span>
                                )}
                                {sitePrice ? (
                                    <span className={`font-semibold text-emerald-800 text-[11px] whitespace-nowrap ${isSiteReloading ? "opacity-75 animate-pulse" : ""}`} title={`Found on ${siteName}: ${formatCurrency(sitePrice)}`}>
                                        {formatCurrency(sitePrice)}
                                    </span>
                                ) : null}
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                                <span
                                    className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] border ${statusBadgeClass} ${isSiteReloading ? "opacity-75 animate-pulse" : ""}`}
                                    title={fullTooltip || undefined}
                                >
                                    {displayStatus}
                                </span>
                                {canReloadSite && (
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (!isSiteReloading && !isReloading) onReloadSite(siteKey);
                                        }}
                                        disabled={isSiteReloading || isReloading}
                                        className={`inline-flex items-center justify-center p-0.5 rounded border transition-colors cursor-pointer ${isSiteReloading
                                            ? "bg-teal-50 text-teal-600 border-teal-300 cursor-wait"
                                            : "bg-white text-gray-400 hover:text-teal-700 hover:border-teal-400 hover:bg-teal-50 border-gray-200"
                                            }`}
                                        title={`Reload ${siteName} price & status`}
                                    >
                                        <RotateCw size={10} className={isSiteReloading ? "animate-spin text-teal-600" : ""} />
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export const mapLeadData = (lead) => {
    // eslint-disable-next-line
    const formatPct = (val, fallback = "") =>
        val !== null && val !== undefined && isFinite(val)
            ? `${val.toFixed(2)} %`
            : fallback;

    const renderMarketField = (value) =>
        lead.marketDataLoading ? <LoadingSpinner /> : value;

    return {
        id: lead.id || "Not Found",
        created_at: formatTime(lead.created_at),
        lead_submitted: formatTime(lead.lead_submitted),
        status: statuses[lead.status] || lead.status,
        full_address: lead.full_address || "Not Found",
        market_score: renderMarketField(
            lead.marketData?.market_score != null ? (
                <span className={getMetricScoreClass(lead.marketData.market_score >= 71 ? 2 : lead.marketData.market_score >= 41 ? 1 : 0)}>
                    {lead.marketData.market_score} / 100
                </span>
            ) : ""
        ),
        market_tier: renderMarketField(
            lead.marketData?.market_tier ? (
                <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${getMarketTierClass(lead.marketData.market_tier)} cursor-help`}>
                    {lead.marketData.market_tier}
                </span>
            ) : ""
        ),
        market_breakdown: renderMarketField(
            lead.marketData?.market_breakdown?.length ? (
                <div className="text-xs cursor-help">
                    {lead.marketData.market_breakdown.map((item) => (
                        <div key={item.key} className={getMetricScoreClass(item.rawScore)}>
                            {item.label}: {item.temperature || "N/A"}{item.value !== null && ` — ${item.points}/${item.maxPoints || item.weight} pts`}
                        </div>
                    ))}
                </div>
            ) : ""
        ),
        ai_review: lead?.ai_review ? (
            <div className="text-sm">
                <div><strong>Quality:</strong> {lead.ai_review.fannie_quality_grade}</div>
                <div><strong>Condition:</strong> {lead.ai_review.fannie_condition_grade}</div>
                <div><strong>Renovation:</strong> {lead.ai_review.renovation_level}</div>

                {lead.ai_review.major_issues.length > 0 && (
                    <div>
                        <strong>Major Issues:</strong>
                        <ul className="list-disc ml-4">
                            {lead.ai_review.major_issues.map((issue, idx) => (
                                <li key={idx}>{issue}</li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        ) : (
            null
        ),
        mailing_address: lead?.propertyDetails?.owner_mailing_label || "Not Found",
        values: (
            <div className="flex flex-wrap gap-2">
                {lead.values?.length > 0 ? (
                    lead.values.map((v) =>
                        v.url ? (
                            <a key={v.id || v.site} href={v.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline text-sm">
                                [{v.site}]
                            </a>
                        ) : null,
                    )
                ) : (
                    <span className="text-gray-400 text-sm"></span>
                )}
            </div>
        ),
        dm_lead_id: lead.dm_lead_id || "Not Found",
        owners: lead.owner?.length ? (
            <div>
                {lead.owner.map((o, idx) => (
                    <div key={o.id || idx} className="font-semibold" style={{ color: getOwnerColor(idx) }}>
                        {o.name}
                    </div>
                ))}
            </div>
        ) : (
            "Not Found"
        ),
        fpPhones: lead.owner?.length
            ? lead.owner.map((owner, idx) => {
                const ownerPhones = lead.fpPhones?.filter((p) => p.owner_id === owner.id).map((p) => p) || [];

                const ownerEmails = [owner.email, owner.email_2, owner.email_3].filter(Boolean);

                if (!ownerPhones.length && !ownerEmails.length) return null;

                return (
                    <div key={idx} className="mb-2">
                        <span className="font-semibold" style={{ color: getOwnerColor(idx) }}>
                            {owner.name || "Unknown Owner"}
                        </span>

                        {ownerPhones.map((phone, index) => {
                            const isCorrect = phone.is_correct;
                            const isDead = !phone.is_correct && phone.is_dead;
                            const isWrong = !phone.is_correct && !phone.is_dead && phone.is_wrong;

                            let textColor = "text-gray-700";
                            if (isCorrect) textColor = "text-green-700";
                            else if (isDead || isWrong) textColor = "text-red-700";

                            return (
                                <div key={index} className={`ml-2 ${textColor} whitespace-pre-line`}>
                                    <span className="flex justify-start items-center">
                                        {formatUSPhone(phone.phone)}

                                        {phone?.type && (
                                            <>
                                                {" - "}
                                                {getPhoneTypeLabel(phone.type)}
                                            </>
                                        )}

                                        {isCorrect ? (
                                            <div className="flex items-center justify-center mx-1 space-x-1">
                                                <span>-</span>
                                                <Check size={20} />
                                            </div>
                                        ) : isDead ? (
                                            <div className="flex items-center justify-center mx-1 space-x-1">
                                                <span>-</span>
                                                <Ban size={15} />
                                            </div>
                                        ) : isWrong ? (
                                            <div className="flex items-center justify-center mx-1 space-x-1">
                                                <span>-</span>
                                                <X size={16} />
                                            </div>
                                        ) : null}
                                    </span>
                                </div>
                            );
                        })}

                        {ownerEmails.length > 0 && <div className="ml-2 text-gray-500 whitespace-pre-line">{ownerEmails.join("\n")}</div>}
                    </div>
                );
            })
            : "Not Found",
        fpPhonesBackup: lead.owner?.length
            ? lead.owner.map((owner, idx) => {
                const ownerPhones = lead.fpPhones?.filter((p) => p.owner_id === owner.id).map((p) => p) || [];

                const ownerEmails = [owner.email, owner.email_2, owner.email_3].filter(Boolean);

                if (!ownerPhones.length && !ownerEmails.length) return null;

                return (
                    <div key={idx} className="mb-2">
                        <span className="font-semibold" style={{ color: getOwnerColor(idx) }}>
                            {owner.name || "Unknown Owner"}
                        </span>

                        {ownerPhones.map((phone, index) => {
                            const isCorrect = phone.is_correct;
                            const isDead = !phone.is_correct && phone.is_dead;
                            const isWrong = !phone.is_correct && !phone.is_dead && phone.is_wrong;

                            let textColor = "text-gray-700";
                            if (isCorrect) textColor = "text-green-700";
                            else if (isDead || isWrong) textColor = "text-red-700";

                            return (
                                <div key={index} className={`ml-2 ${textColor} whitespace-pre-line`}>
                                    <span className="flex justify-start items-center">
                                        {formatUSPhone(phone.phone)}

                                        {phone?.type && (
                                            <>
                                                {" - "}
                                                {getPhoneTypeLabel(phone.type)}
                                            </>
                                        )}

                                        {isCorrect ? (
                                            <div className="flex items-center justify-center mx-1 space-x-1">
                                                <span>-</span>
                                                <Check size={20} />
                                            </div>
                                        ) : isDead ? (
                                            <div className="flex items-center justify-center mx-1 space-x-1">
                                                <span>-</span>
                                                <Ban size={15} />
                                            </div>
                                        ) : isWrong ? (
                                            <div className="flex items-center justify-center mx-1 space-x-1">
                                                <span>-</span>
                                                <X size={16} />
                                            </div>
                                        ) : null}
                                    </span>
                                </div>
                            );
                        })}

                        {ownerEmails.length > 0 && <div className="ml-2 text-gray-500 whitespace-pre-line">{ownerEmails.join("\n")}</div>}
                    </div>
                );
            })
            : "Not Found",
        edqPhones: lead.edqPhones?.length ? lead.edqPhones.map((p) => `(${p.type} ${p.status && `- ${p.status}`} - ${p.phone} )`).join("\n") : "Not Found",
        confirmedAI: (() => {
            const confirmed = lead.ai?.find((a) => a.ai_status === "CONFIRMED");
            return confirmed ? `${confirmed.ai_status} (${confirmed.phone})` : "Not Found";
        })(),
        ghlSaved: lead.owner?.length
            ? lead.owner
                .filter((o) => o.added_ghl)
                .map((owner, idx) => {
                    const ownerPhones = lead.fpPhones?.filter((p) => p.owner_id === owner.id && p.added_ghl).map((p) => p) || [];

                    const ownerEmails = [owner.email, owner.email_2, owner.email_3].filter(Boolean);

                    if (!ownerPhones.length && !ownerEmails.length) return null;

                    return (
                        <div key={idx} className="mb-2">
                            <span className="font-semibold" style={{ color: getOwnerColor(idx) }}>
                                {owner.name || "Unknown Owner"}
                            </span>

                            {ownerPhones.map((phone, index) => {
                                const isCorrect = phone.is_correct;
                                const isDead = !phone.is_correct && phone.is_dead;
                                const isWrong = !phone.is_correct && !phone.is_dead && phone.is_wrong;

                                let textColor = "text-gray-700";
                                if (isCorrect) textColor = "text-green-700";
                                else if (isDead || isWrong) textColor = "text-red-700";

                                return (
                                    <div key={index} className={`ml-2 ${textColor} whitespace-pre-line`}>
                                        <span className="flex justify-start items-center">
                                            {formatUSPhone(phone.phone)}

                                            {phone?.type && (
                                                <>
                                                    {" - "}
                                                    {getPhoneTypeLabel(phone.type)}
                                                </>
                                            )}

                                            {isCorrect ? (
                                                <div className="flex items-center justify-center mx-1 space-x-1">
                                                    <span>-</span>
                                                    <Check size={20} />
                                                </div>
                                            ) : isDead ? (
                                                <div className="flex items-center justify-center mx-1 space-x-1">
                                                    <span>-</span>
                                                    <Ban size={15} />
                                                </div>
                                            ) : isWrong ? (
                                                <div className="flex items-center justify-center mx-1 space-x-1">
                                                    <span>-</span>
                                                    <X size={16} />
                                                </div>
                                            ) : null}
                                        </span>
                                    </div>
                                );
                            })}

                            {ownerEmails.length > 0 && <div className="ml-2 text-gray-500 whitespace-pre-line">{ownerEmails[0]}</div>}
                        </div>
                    );
                })
            : "Not Found",
        source_m: lead.source_m ? (
            <div className="whitespace-pre-line">
                {lead.source_m.name && <div>Name: {lead.source_m.name}</div>}
                {lead.source_m.phone && <div>Phone: {lead.source_m.phone}</div>}
                {lead.source_m.email && <div>Email: {lead.source_m.email}</div>}
            </div>
        ) : (
            ""
        ),
        zip_population: renderMarketField(lead.marketData?.population_in_zip ?? ""),
        city_population: renderMarketField(lead.marketData?.population_in_city ?? ""),
        county_population: renderMarketField(lead.marketData?.population_in_county ?? ""),
        sale_props: renderMarketField(lead.marketData?.sale_props ?? ""),
        pending_props: renderMarketField(lead.marketData?.pending_props ?? ""),
        sale_pending: renderMarketField(lead.marketData?.total_props ?? ""),
        pending_ratio: renderMarketField(
            lead.marketData?.pending_ratio ? (
                renderMetricWithTemp(
                    `${lead.marketData.pending_ratio}%`,
                    lead.marketData.pending_ratio_temperature,
                    lead.marketData.pending_ratio_pts,
                    "Pending-to-Active Ratio",
                    "(pending / active) × 100",
                    { "Active": lead.marketData.sale_props, "Pending": lead.marketData.pending_props }
                )
            ) : "N/A"
        ),
        months_inventory: renderMarketField(
            lead.marketData?.months_inventory != null ? (
                renderMetricWithTemp(
                    lead.marketData.months_inventory,
                    lead.marketData.months_inventory_temperature,
                    lead.marketData.months_inventory_pts,
                    "Months of Inventory",
                    "Months supply of homes (based on current sales pace)",
                    { "Months": lead.marketData.months_inventory }
                )
            ) : null
        ),
        flipped_props: renderMarketField(
            <div className="flex flex-col items-center justify-center gap-2">
                <span>{lead.marketData?.flipped_properties_in_area ?? ""}</span>
                <a
                    href={`/addr?address=${encodeURI(lead.full_address)}&flippersLimit=10&landlordsLimit=10`}
                    className="cursor-pointer text-blue-500 hover:text-blue-800 visited:text-purple-700"
                >
                    [ Details ]
                </a>
            </div>
        ),
        landlords: renderMarketField(
            <div className="flex flex-col items-center justify-center gap-2">
                <span>{lead.marketData?.landlords_in_area ?? ""}</span>
                <a
                    href={`/addr?address=${encodeURI(lead.full_address)}&flippersLimit=10&landlordsLimit=10`}
                    className="cursor-pointer text-blue-500 hover:text-blue-800 visited:text-purple-700"
                >
                    [ Details ]
                </a>
            </div>
        ),
        landlord: renderMarketField(
            lead.marketData?.current_owner_is_a_landlord ? (
                <div className={`${lead.marketData.current_owner_is_a_landlord === "True" ? "text-green-600" : "text-red-600"} font-medium text-sm`}>
                    {lead.marketData.current_owner_is_a_landlord}
                </div>
            ) : ""
        ),
        ownerd_props: renderMarketField(lead.marketData?.current_owner_owned_properties ?? ""),
        propwire_props: lead?.zip_population?.propwire_props,
        propwire_active_props: lead?.zip_population?.propwire_active_props,
        propwire_percentage: (
            <Tooltip
                title={buildMetricTooltip({
                    title: "Zillow Propwire Active Percentage",
                    formula: "(Active Zillow / Total Propwire) * 100",
                    computed: () => {
                        const total = lead?.zip_population?.propwire_props;
                        const active = (lead?.zipStats?.sale + lead?.zipStats?.pending);

                        if (!total || total === 0) return "Invalid data";

                        const result = ((active / total) * 100).toFixed(2);

                        return `(${active} / ${total}) * 100 = ${result}%`;
                    },
                    values: {
                        "Total Properties": lead?.zip_population?.propwire_props,
                        "Active Properties": (lead?.zipStats?.sale + lead?.zipStats?.pending),
                        "Formula": "Active ÷ Total × 100"
                    }
                })}
            >
                {lead?.zip_population?.propwire_props &&
                    lead?.zipStats?.sale &&
                    lead?.zipStats?.sale != null &&
                    lead?.zipStats?.sale !== 0
                    ? ((lead?.zipStats?.sale + lead?.zipStats?.pending) / lead.zip_population.propwire_props * 100).toFixed(2) + "%" : ""}

            </Tooltip>
        ),
        for_sale_inventory: lead?.zipStats?.sale ?? null,
        median_sale_price: renderMarketField(lead.marketData?.median_sale_price),
        median_list_price: renderMarketField(lead.marketData?.median_list_price),
        median_sale_to_list_ratio: renderMarketField(
            lead.marketData?.sales_to_list != null ? (
                renderMetricWithTemp(
                    `${lead.marketData.sales_to_list}%`,
                    lead.marketData.sales_to_list_temperature,
                    lead.marketData.sales_to_list_pts,
                    "Sale-to-List Ratio",
                    "final sale price / last list price × 100",
                    { "Sale to List": lead.marketData.sales_to_list }
                )
            ) : "—"
        ),
        percent_sales_over_list_price: renderMarketField(
            lead.marketData?.sales_over_list != null ? (
                renderMetricWithTemp(
                    `${lead.marketData.sales_over_list}%`,
                    lead.marketData.sales_over_list_temperature,
                    lead.marketData.sales_over_list_pts,
                    "Sales Over List Price",
                    "Percentage of homes sold above list price",
                    { "Sales Over List": lead.marketData.sales_over_list }
                )
            ) : null
        ),
        median_days_to_pending: renderMarketField(
            lead.marketData?.days_to_pending != null ? (
                renderMetricWithTemp(
                    lead.marketData.days_to_pending,
                    lead.marketData.days_to_pending_temperature,
                    lead.marketData.days_to_pending_pts,
                    "Days to Pending",
                    "Median days from listing to pending status",
                    { "Days": lead.marketData.days_to_pending }
                )
            ) : null
        ),
        median_days_on_market: renderMarketField(lead.marketData?.days_on_market ?? null),
        metropolitanmicropolitanstatis: renderMarketField(lead.marketData?.politan_statis),
        csatitle: renderMarketField(lead.marketData?.csa_title),
        hh_rank: renderMarketField(lead.marketData?.hh_rank_1_300),
        hotness_rank: renderMarketField(lead.marketData?.hotness_rank_1300),
        hotness_score: renderMarketField(lead.marketData?.hotness_score),
        supply_score: renderMarketField(lead.marketData?.supply_score),
        demand_score: renderMarketField(lead.marketData?.demand_score),
        page_view_count_per_property_vs_us: renderMarketField(lead.marketData?.page_view_count_per_property_vs_us),
        // new ai data 
        within_city_limits:
            lead?.ai_data?.within_city_limits !== null && lead?.ai_data?.within_city_limits !== undefined ? (
                <span>{lead?.ai_data.within_city_limits ? "Yes" : "No"}</span>
            ) : (
                ""
            ),

        metro_details:
            lead?.ai_data?.nearest_metro ||
                lead?.ai_data?.metro_distance_miles ||
                lead?.ai_data?.metro_distance_minutes ||
                lead?.ai_data?.metro_population ? (
                <div>
                    {lead?.ai_data?.nearest_metro && (
                        <div>
                            <strong>Metro:</strong> {lead?.ai_data.nearest_metro}
                        </div>
                    )}
                    {lead?.ai_data?.metro_distance_miles != null && (
                        <div>
                            <strong>Distance:</strong> {lead?.ai_data.metro_distance_miles} mi
                        </div>
                    )}
                    {lead?.ai_data?.metro_distance_minutes != null && (
                        <div>
                            <strong>Time:</strong> {lead?.ai_data.metro_distance_minutes} min
                        </div>
                    )}
                    {lead?.ai_data?.metro_population != null && (
                        <div>
                            <strong>Population:</strong> {lead?.ai_data.metro_population.toLocaleString()}
                        </div>
                    )}
                </div>
            ) : (
                ""
            ),

        nearby_density:
            lead?.ai_data?.nearby_density != null ? (
                <span>{lead?.ai_data.nearby_density.toLocaleString()}</span>
            ) : (
                ""
            ),

        area_type:
            lead?.ai_data?.area_type ? (
                <span style={{ textTransform: "capitalize" }}>
                    {lead?.ai_data.area_type}
                </span>
            ) : (
                ""
            ),
    }
};

export const mapLeadDataNotFound = (lead = {}) => {
    const result = {};

    // Owner name
    result.name = [lead.first_name, lead.last_name]
        .filter(Boolean)
        .join(" ") || "Not Found";

    // Backward-compatible fields used by property search URL
    result.phone = lead.phone_number || "Not Found";
    result.email = lead.email || "Not Found";
    result.beds = lead.bedrooms ?? "Not Found";
    result.baths = lead.bathrooms ?? "Not Found";
    result.received = lead.received_time || lead.created_at || "Not Found";

    // Market score
    if (lead.marketDataLoading) {
        result.market_score = <LoadingSpinner />;
    } else if (lead.marketData?.market_score != null) {
        const score = Number(lead.marketData.market_score);

        const cls = getMetricScoreClass(
            score >= 71 ? 2 : score >= 41 ? 1 : 0
        );

        result.market_score = (
            <span className={cls}>{score} / 100</span>
        );
    } else {
        result.market_score = "Not Found";
    }

    // Process API fields
    Object.keys(lead).forEach((key) => {
        if (key === "market_score") return;

        const value = lead[key];

        if (value === null || value === undefined || value === "") {
            result[key] = "Not Found";
            return;
        }

        if (
            key.includes("created_at") ||
            key.includes("updated_at") ||
            key.includes("appointment_date") ||
            key.includes("date_posted") ||
            key.includes("received_time") ||
            key.includes("lead_submitted")
        ) {
            result[key] = formatTime(value);
            return;
        }

        // Format boolean-like database flags
        if (
            [
                "listed",
                "verified",
                "rejected_lead",
                "refund_request",
                "mortgage_status",
            ].includes(key)
        ) {
            result[key] = value === 1 ? "Yes" : value === 0 ? "No" : value;
            return;
        }

        // Format numeric valuation
        if (key === "valuation") {
            const numericValue = Number(value);

            result[key] = Number.isNaN(numericValue)
                ? value
                : `$${numericValue.toLocaleString()}`;

            return;
        }

        result[key] = value;
    });

    return result;
};

const buildMetricTooltip = ({ title, formula, computed, values }) => {
    const computedValue =
        typeof computed === "function" ? computed() : computed;

    return (
        <div>
            <div className="text-center"><b>{title}</b></div>

            <div><b>Formula</b></div>
            <div>{formula}</div>

            <div>
                {computedValue && (
                    <>
                        <b>{computedValue}</b>
                    </>
                )}
            </div>

            <div><b>Values</b></div>

            {Object.entries(values || {}).map(([key, val]) => (
                <div key={key} className="grid grid-cols-2">
                    <span>{key}:</span>
                    <span>{val ?? "N/A"}</span>
                </div>
            ))}
        </div>
    );
};

export function parsePhone(input) {
    if (!input || typeof input !== "string") return null;

    let str = input.trim();

    // 1️⃣ Extract type at end (like Work, Mobile, etc.)
    let phonePart = str;
    let typePart = null;

    const typeMatch = str.match(/(?:\s*[-]?\s*)([A-Za-z]+)$/);
    if (typeMatch) {
        typePart = typeMatch[1].trim();
        phonePart = str.slice(0, typeMatch.index).trim();
    }

    // 2️⃣ Extract only digits
    let digits = phonePart.replace(/\D/g, "");

    // Remove leading "1" if 11 digits
    if (digits.length === 11 && digits.startsWith("1")) {
        digits = digits.slice(1);
    }

    // 3️⃣ Validate
    if (digits.length !== 10) {
        return { phone: null, type: typePart };
    }

    return { phone: digits, type: typePart || null };
}


const slugify = (str) => str?.trim().replace(/\s+/g, "-").toLowerCase() || "";
export const makeFpsLink = (addr, zip, city, state) => (addr && zip && city && state ? `https://www.fastpeoplesearch.com/address/${slugify(addr)}_${slugify(city)}-${slugify(state)}-${zip}` : null);


// utils/phoneUtils.js
export const getPhoneTypeLabel = (type) => {
    if (!type) return "";
    const map = {
        L: "Landline",
        F: "Landline",
        W: "Mobile",
        M: "Landline",
        H: "Landline",
        O: "Landline",
    };
    return map[type] || type;
};


export const propertyTypeLabels = {
    UNCATEGORIZED: "Uncategorized",
    COMMERCIAL: "Commercial",
    OTHER: "Other",
    LAND: "Land",
    SFR: "Single-Family House",
    MOBILE: "Mobile Homes",
    MFH_2_TO_4: "Multi-Family (2-4)",
    CONDO: "Condos/Townhouse",
    MFH_5_PLUS: "Multi-Family (5+)",
};

export const getPropertyTypeLabel = (value) => {
    return propertyTypeLabels[value] || value || "Uncategorized";
};

export function updateLocalStorageRolling(key, id, limit = 50) {
    const stored = JSON.parse(localStorage.getItem(key) || "[]");
    const updated = [...stored.filter(item => item !== id), id];
    if (updated.length > limit) updated.splice(0, updated.length - limit);
    localStorage.setItem(key, JSON.stringify(updated));
    return updated;
}

const excludeWords = [
    "UNKNOW TENANT",
    "UNKNOW",
    "ASSOCIATION",
    "UNKNOWN",
    "CITY OF",
    "UNITED STATE",
    "STATE OF FLORIDA",
    "DEPARTMENT OF TREASURY",
    "UNKNOWN TENANT",
    "UNKNOWN PARTY IN POSITION",
    "UNKNOWN PARTY",
    "TENANT",
    "UNKNOWN PERSON IN POSITION",
    "UNKNOWN PERSON",
    "ANY UNKNOWN PARTIES",
    "ANY AND ALL UNKNOWN PARTIES",
    "UNKNOWN PARTIES",
    "JOHN DOE",
    "JANE DOE",
    "ESTATE OF",
    "UNKNOWN HEIRS",
    "UNKNOWN SPOUSE",
    "LLC",
    "TRUST",
    "COMPANY",
];

export const containsExcludedWord = (name = "") =>
    excludeWords.some(word =>
        name.toUpperCase().includes(word)
    );

export const formatUSPhone = (phone) => {
    if (!phone) return "";

    const digits = phone.replace(/\D/g, ""); // remove non-digits

    // remove leading country code 1 if length is 11
    const normalized = digits.length === 11 && digits.startsWith("1")
        ? digits.slice(1)
        : digits;

    if (normalized.length !== 10) return phone;

    const area = normalized.slice(0, 3);
    const prefix = normalized.slice(3, 6);
    const line = normalized.slice(6);

    return `(${area}) ${prefix} ${line}`;
};

const areaCodeInfo = {
    "201": { "state": "NJ", "city": "Hackensack/Jersey City", "timezone": "America/New_York" },
    "202": { "state": "DC", "city": "Washington", "timezone": "America/New_York" },
    "203": { "state": "CT", "city": "Bridgeport/New Haven", "timezone": "America/New_York" },
    "205": { "state": "AL", "city": "Birmingham", "timezone": "America/Chicago" },
    "206": { "state": "WA", "city": "Seattle", "timezone": "America/Los_Angeles" },
    "207": { "state": "ME", "city": "All of Maine", "timezone": "America/New_York" },
    "208": { "state": "ID", "city": "All of Idaho", "timezone": "America/Denver" },
    "209": { "state": "CA", "city": "Stockton/Modesto", "timezone": "America/Los_Angeles" },
    "210": { "state": "TX", "city": "San Antonio", "timezone": "America/Chicago" },
    "212": { "state": "NY", "city": "New York City (Manhattan)", "timezone": "America/New_York" },
    "213": { "state": "CA", "city": "Los Angeles", "timezone": "America/Los_Angeles" },
    "214": { "state": "TX", "city": "Dallas", "timezone": "America/Chicago" },
    "215": { "state": "PA", "city": "Philadelphia", "timezone": "America/New_York" },
    "216": { "state": "OH", "city": "Cleveland", "timezone": "America/New_York" },
    "217": { "state": "IL", "city": "Springfield/Champaign", "timezone": "America/Chicago" },
    "218": { "state": "MN", "city": "Duluth", "timezone": "America/Chicago" },
    "219": { "state": "IN", "city": "Gary/Hammond", "timezone": "America/Chicago" },
    "220": { "state": "OH", "city": "Central/Southeastern Ohio", "timezone": "America/New_York" },
    "223": { "state": "PA", "city": "Harrisburg/Lancaster", "timezone": "America/New_York" },
    "224": { "state": "IL", "city": "North/Northwest Chicago Suburbs", "timezone": "America/Chicago" },
    "225": { "state": "LA", "city": "Baton Rouge", "timezone": "America/Chicago" },
    "226": { "state": "ON", "city": "Southwestern Ontario", "timezone": "America/Toronto" },
    "228": { "state": "MS", "city": "Gulfport/Biloxi", "timezone": "America/Chicago" },
    "229": { "state": "GA", "city": "Albany/Valdosta", "timezone": "America/New_York" },
    "231": { "state": "MI", "city": "Muskegon/Traverse City", "timezone": "America/New_York" },
    "234": { "state": "OH", "city": "Akron/Canton", "timezone": "America/New_York" },
    "236": { "state": "BC", "city": "British Columbia", "timezone": "America/Vancouver" },
    "239": { "state": "FL", "city": "Fort Myers/Naples", "timezone": "America/New_York" },
    "240": { "state": "MD", "city": "Western/Southern Maryland", "timezone": "America/New_York" },
    "242": { "state": "BS", "city": "The Bahamas", "timezone": "America/Nassau" },
    "246": { "state": "BB", "city": "Barbados", "timezone": "America/Barbados" },
    "248": { "state": "MI", "city": "Oakland County", "timezone": "America/New_York" },
    "249": { "state": "ON", "city": "Northeastern Ontario", "timezone": "America/Toronto" },
    "250": { "state": "BC", "city": "British Columbia", "timezone": "America/Vancouver" },
    "251": { "state": "AL", "city": "Mobile", "timezone": "America/Chicago" },
    "252": { "state": "NC", "city": "Eastern North Carolina", "timezone": "America/New_York" },
    "253": { "state": "WA", "city": "Tacoma", "timezone": "America/Los_Angeles" },
    "254": { "state": "TX", "city": "Waco/Killeen", "timezone": "America/Chicago" },
    "256": { "state": "AL", "city": "Huntsville/Anniston", "timezone": "America/Chicago" },
    "260": { "state": "IN", "city": "Fort Wayne", "timezone": "America/New_York" },
    "262": { "state": "WI", "city": "Kenosha/Racine", "timezone": "America/Chicago" },
    "264": { "state": "AI", "city": "Anguilla", "timezone": "America/Anguilla" },
    "267": { "state": "PA", "city": "Philadelphia area", "timezone": "America/New_York" },
    "268": { "state": "AG", "city": "Antigua and Barbuda", "timezone": "America/Antigua" },
    "269": { "state": "MI", "city": "Kalamazoo/Battle Creek", "timezone": "America/New_York" },
    "270": { "state": "KY", "city": "Western Kentucky", "timezone": "America/Chicago" },
    "272": { "state": "PA", "city": "Northeastern Pennsylvania", "timezone": "America/New_York" },
    "274": { "state": "WI", "city": "Wisconsin", "timezone": "America/Chicago" },
    "276": { "state": "VA", "city": "Southwestern Virginia", "timezone": "America/New_York" },
    "278": { "state": "MI", "city": "Michigan", "timezone": "America/New_York" },
    "279": { "state": "CA", "city": "Sacramento area", "timezone": "America/Los_Angeles" },
    "281": { "state": "TX", "city": "Houston", "timezone": "America/Chicago" },
    "283": { "state": "OH", "city": "Cincinnati area", "timezone": "America/New_York" },
    "284": { "state": "VG", "city": "British Virgin Islands", "timezone": "America/Tortola" },
    "289": { "state": "ON", "city": "Southern Ontario", "timezone": "America/Toronto" },
    "301": { "state": "MD", "city": "Western Maryland", "timezone": "America/New_York" },
    "302": { "state": "DE", "city": "All of Delaware", "timezone": "America/New_York" },
    "303": { "state": "CO", "city": "Denver", "timezone": "America/Denver" },
    "304": { "state": "WV", "city": "West Virginia", "timezone": "America/New_York" },
    "305": { "state": "FL", "city": "Miami/Key West", "timezone": "America/New_York" },
    "306": { "state": "SK", "city": "Saskatchewan", "timezone": "America/Regina" },
    "307": { "state": "WY", "city": "All of Wyoming", "timezone": "America/Denver" },
    "308": { "state": "NE", "city": "Western Nebraska", "timezone": "America/Denver" },
    "309": { "state": "IL", "city": "Peoria/Bloomington", "timezone": "America/Chicago" },
    "310": { "state": "CA", "city": "West Los Angeles", "timezone": "America/Los_Angeles" },
    "312": { "state": "IL", "city": "Chicago Loop", "timezone": "America/Chicago" },
    "313": { "state": "MI", "city": "Detroit", "timezone": "America/New_York" },
    "314": { "state": "MO", "city": "St. Louis", "timezone": "America/Chicago" },
    "315": { "state": "NY", "city": "Syracuse/Utica", "timezone": "America/New_York" },
    "316": { "state": "KS", "city": "Wichita", "timezone": "America/Chicago" },
    "317": { "state": "IN", "city": "Indianapolis", "timezone": "America/New_York" },
    "318": { "state": "LA", "city": "Shreveport/Monroe", "timezone": "America/Chicago" },
    "319": { "state": "IA", "city": "Cedar Rapids/Iowa City", "timezone": "America/Chicago" },
    "320": { "state": "MN", "city": "St. Cloud", "timezone": "America/Chicago" },
    "321": { "state": "FL", "city": "Orlando/Melbourne", "timezone": "America/New_York" },
    "323": { "state": "CA", "city": "Los Angeles", "timezone": "America/Los_Angeles" },
    "324": { "state": "FL", "city": "Florida", "timezone": "America/New_York" },
    "325": { "state": "TX", "city": "Abilene/San Angelo", "timezone": "America/Chicago" },
    "327": { "state": "AR", "city": "Arkansas", "timezone": "America/Chicago" },
    "329": { "state": "NY", "city": "New York", "timezone": "America/New_York" },
    "330": { "state": "OH", "city": "Akron/Canton", "timezone": "America/New_York" },
    "331": { "state": "IL", "city": "Aurora/Naperville", "timezone": "America/Chicago" },
    "332": { "state": "NY", "city": "New York City", "timezone": "America/New_York" },
    "334": { "state": "AL", "city": "Montgomery/Dothan", "timezone": "America/Chicago" },
    "336": { "state": "NC", "city": "Greensboro/Winston-Salem", "timezone": "America/New_York" },
    "337": { "state": "LA", "city": "Lafayette/Lake Charles", "timezone": "America/Chicago" },
    "339": { "state": "MA", "city": "Eastern Massachusetts", "timezone": "America/New_York" },
    "340": { "state": "VI", "city": "US Virgin Islands", "timezone": "America/St_Thomas" },
    "341": { "state": "CA", "city": "Oakland area", "timezone": "America/Los_Angeles" },
    "343": { "state": "ON", "city": "Eastern Ontario", "timezone": "America/Toronto" },
    "345": { "state": "KY", "city": "Cayman Islands", "timezone": "America/Cayman" },
    "346": { "state": "TX", "city": "Houston", "timezone": "America/Chicago" },
    "347": { "state": "NY", "city": "New York City", "timezone": "America/New_York" },
    "350": { "state": "CA", "city": "Central California", "timezone": "America/Los_Angeles" },
    "351": { "state": "MA", "city": "Northeastern Massachusetts", "timezone": "America/New_York" },
    "352": { "state": "FL", "city": "Gainesville/Ocala", "timezone": "America/New_York" },
    "353": { "state": "WI", "city": "Wisconsin", "timezone": "America/Chicago" },
    "354": { "state": "QC", "city": "Quebec", "timezone": "America/Montreal" },
    "360": { "state": "WA", "city": "Western Washington", "timezone": "America/Los_Angeles" },
    "361": { "state": "TX", "city": "Corpus Christi", "timezone": "America/Chicago" },
    "363": { "state": "NY", "city": "New York", "timezone": "America/New_York" },
    "364": { "state": "KY", "city": "Western Kentucky", "timezone": "America/Chicago" },
    "365": { "state": "ON", "city": "Ontario", "timezone": "America/Toronto" },
    "367": { "state": "QC", "city": "Quebec", "timezone": "America/Montreal" },
    "369": { "state": "CA", "city": "Northern California", "timezone": "America/Los_Angeles" },
    "380": { "state": "OH", "city": "Ohio", "timezone": "America/New_York" },
    "385": { "state": "UT", "city": "Utah", "timezone": "America/Denver" },
    "386": { "state": "FL", "city": "Daytona Beach/Lake City", "timezone": "America/New_York" },
    "401": { "state": "RI", "city": "All of Rhode Island", "timezone": "America/New_York" },
    "402": { "state": "NE", "city": "Omaha/Lincoln", "timezone": "America/Chicago" },
    "403": { "state": "AB", "city": "Southern Alberta", "timezone": "America/Edmonton" },
    "404": { "state": "GA", "city": "Atlanta", "timezone": "America/New_York" },
    "405": { "state": "OK", "city": "Oklahoma City", "timezone": "America/Chicago" },
    "406": { "state": "MT", "city": "All of Montana", "timezone": "America/Denver" },
    "407": { "state": "FL", "city": "Orlando", "timezone": "America/New_York" },
    "408": { "state": "CA", "city": "San Jose", "timezone": "America/Los_Angeles" },
    "409": { "state": "TX", "city": "Beaumont/Galveston", "timezone": "America/Chicago" },
    "410": { "state": "MD", "city": "Baltimore/Annapolis", "timezone": "America/New_York" },
    "412": { "state": "PA", "city": "Pittsburgh", "timezone": "America/New_York" },
    "413": { "state": "MA", "city": "Western Massachusetts", "timezone": "America/New_York" },
    "414": { "state": "WI", "city": "Milwaukee", "timezone": "America/Chicago" },
    "415": { "state": "CA", "city": "San Francisco", "timezone": "America/Los_Angeles" },
    "416": { "state": "ON", "city": "Toronto", "timezone": "America/Toronto" },
    "417": { "state": "MO", "city": "Springfield/Joplin", "timezone": "America/Chicago" },
    "418": { "state": "QC", "city": "Quebec City", "timezone": "America/Montreal" },
    "419": { "state": "OH", "city": "Toledo", "timezone": "America/New_York" },
    "423": { "state": "TN", "city": "Chattanooga/Kingsport", "timezone": "America/New_York" },
    "424": { "state": "CA", "city": "Los Angeles County", "timezone": "America/Los_Angeles" },
    "425": { "state": "WA", "city": "Bellevue/Everett", "timezone": "America/Los_Angeles" },
    "428": { "state": "NB", "city": "New Brunswick", "timezone": "America/Moncton" },
    "430": { "state": "TX", "city": "Northeast Texas", "timezone": "America/Chicago" },
    "431": { "state": "MB", "city": "Manitoba", "timezone": "America/Winnipeg" },
    "432": { "state": "TX", "city": "Midland/Odessa", "timezone": "America/Chicago" },
    "434": { "state": "VA", "city": "Charlottesville/Lynchburg", "timezone": "America/New_York" },
    "435": { "state": "UT", "city": "Southern and Eastern Utah", "timezone": "America/Denver" },
    "436": { "state": "OH", "city": "Ohio", "timezone": "America/New_York" },
    "437": { "state": "ON", "city": "Toronto area", "timezone": "America/Toronto" },
    "438": { "state": "QC", "city": "Montreal area", "timezone": "America/Montreal" },
    "440": { "state": "OH", "city": "Cleveland area", "timezone": "America/New_York" },
    "441": { "state": "BM", "city": "Bermuda", "timezone": "Atlantic/Bermuda" },
    "442": { "state": "CA", "city": "Southern California", "timezone": "America/Los_Angeles" },
    "443": { "state": "MD", "city": "Baltimore area", "timezone": "America/New_York" },
    "445": { "state": "PA", "city": "Philadelphia area", "timezone": "America/New_York" },
    "447": { "state": "IL", "city": "Central Illinois", "timezone": "America/Chicago" },
    "448": { "state": "FL", "city": "Florida panhandle", "timezone": "America/New_York" },
    "450": { "state": "QC", "city": "Southern Quebec", "timezone": "America/Montreal" },
    "457": { "state": "LA", "city": "Louisiana", "timezone": "America/Chicago" },
    "458": { "state": "OR", "city": "Oregon", "timezone": "America/Los_Angeles" },
    "463": { "state": "IN", "city": "Central Indiana", "timezone": "America/New_York" },
    "464": { "state": "IL", "city": "Chicago suburbs", "timezone": "America/Chicago" },
    "468": { "state": "QC", "city": "Quebec", "timezone": "America/Montreal" },
    "469": { "state": "TX", "city": "Dallas area", "timezone": "America/Chicago" },
    "470": { "state": "GA", "city": "Atlanta area", "timezone": "America/New_York" },
    "472": { "state": "NC", "city": "North Carolina", "timezone": "America/New_York" },
    "473": { "state": "GD", "city": "Grenada", "timezone": "America/Grenada" },
    "474": { "state": "SK", "city": "Saskatchewan", "timezone": "America/Regina" },
    "475": { "state": "CT", "city": "Southwestern CT", "timezone": "America/New_York" },
    "478": { "state": "GA", "city": "Macon/Dublin", "timezone": "America/New_York" },
    "479": { "state": "AR", "city": "Fayetteville/Fort Smith", "timezone": "America/Chicago" },
    "480": { "state": "AZ", "city": "Mesa/Scottsdale/Tempe", "timezone": "America/Phoenix" },
    "484": { "state": "PA", "city": "Eastern Pennsylvania", "timezone": "America/New_York" },
    "501": { "state": "AR", "city": "Central Arkansas", "timezone": "America/Chicago" },
    "502": { "state": "KY", "city": "Louisville", "timezone": "America/New_York" },
    "503": { "state": "OR", "city": "Portland", "timezone": "America/Los_Angeles" },
    "504": { "state": "LA", "city": "New Orleans", "timezone": "America/Chicago" },
    "505": { "state": "NM", "city": "Albuquerque/Santa Fe", "timezone": "America/Denver" },
    "506": { "state": "NB", "city": "New Brunswick", "timezone": "America/Moncton" },
    "507": { "state": "MN", "city": "Rochester/Mankato", "timezone": "America/Chicago" },
    "508": { "state": "MA", "city": "Central Massachusetts", "timezone": "America/New_York" },
    "509": { "state": "WA", "city": "Eastern Washington", "timezone": "America/Los_Angeles" },
    "510": { "state": "CA", "city": "Oakland/Fremont/Berkeley", "timezone": "America/Los_Angeles" },
    "512": { "state": "TX", "city": "Austin", "timezone": "America/Chicago" },
    "513": { "state": "OH", "city": "Cincinnati", "timezone": "America/New_York" },
    "514": { "state": "QC", "city": "Montreal", "timezone": "America/Montreal" },
    "515": { "state": "IA", "city": "Des Moines", "timezone": "America/Chicago" },
    "516": { "state": "NY", "city": "Long Island (Nassau County)", "timezone": "America/New_York" },
    "517": { "state": "MI", "city": "Lansing/Jackson", "timezone": "America/New_York" },
    "518": { "state": "NY", "city": "Albany", "timezone": "America/New_York" },
    "519": { "state": "ON", "city": "Southwestern Ontario", "timezone": "America/Toronto" },
    "520": { "state": "AZ", "city": "Southern Arizona", "timezone": "America/Phoenix" },
    "530": { "state": "CA", "city": "Northern California", "timezone": "America/Los_Angeles" },
    "531": { "state": "NE", "city": "Omaha/Lincoln", "timezone": "America/Chicago" },
    "539": { "state": "OK", "city": "Eastern Oklahoma", "timezone": "America/Chicago" },
    "540": { "state": "VA", "city": "Western Virginia", "timezone": "America/New_York" },
    "541": { "state": "OR", "city": "Oregon", "timezone": "America/Los_Angeles" },
    "548": { "state": "ON", "city": "Ontario", "timezone": "America/Toronto" },
    "551": { "state": "NJ", "city": "Northern New Jersey", "timezone": "America/New_York" },
    "559": { "state": "CA", "city": "Fresno/Visalia", "timezone": "America/Los_Angeles" },
    "561": { "state": "FL", "city": "West Palm Beach/Boca Raton", "timezone": "America/New_York" },
    "562": { "state": "CA", "city": "Long Beach/Whittier", "timezone": "America/Los_Angeles" },
    "563": { "state": "IA", "city": "Eastern Iowa", "timezone": "America/Chicago" },
    "567": { "state": "OH", "city": "Northwestern Ohio", "timezone": "America/New_York" },
    "570": { "state": "PA", "city": "Northeastern Pennsylvania", "timezone": "America/New_York" },
    "571": { "state": "VA", "city": "Northern Virginia", "timezone": "America/New_York" },
    "573": { "state": "MO", "city": "Central Missouri", "timezone": "America/Chicago" },
    "574": { "state": "IN", "city": "Northern Indiana", "timezone": "America/New_York" },
    "575": { "state": "NM", "city": "Southern New Mexico", "timezone": "America/Denver" },
    "579": { "state": "QC", "city": "Quebec", "timezone": "America/Montreal" },
    "580": { "state": "OK", "city": "Western Oklahoma", "timezone": "America/Chicago" },
    "581": { "state": "QC", "city": "Quebec", "timezone": "America/Montreal" },
    "584": { "state": "MB", "city": "Manitoba", "timezone": "America/Winnipeg" },
    "585": { "state": "NY", "city": "Rochester", "timezone": "America/New_York" },
    "586": { "state": "MI", "city": "Macomb County", "timezone": "America/New_York" },
    "587": { "state": "AB", "city": "Alberta", "timezone": "America/Edmonton" },
    "601": { "state": "MS", "city": "Central Mississippi", "timezone": "America/Chicago" },
    "602": { "state": "AZ", "city": "Phoenix", "timezone": "America/Phoenix" },
    "603": { "state": "NH", "city": "All of New Hampshire", "timezone": "America/New_York" },
    "604": { "state": "BC", "city": "Lower Mainland", "timezone": "America/Vancouver" },
    "605": { "state": "SD", "city": "All of South Dakota", "timezone": "America/Chicago" },
    "606": { "state": "KY", "city": "Eastern Kentucky", "timezone": "America/New_York" },
    "607": { "state": "NY", "city": "Southern Tier", "timezone": "America/New_York" },
    "608": { "state": "WI", "city": "Southwestern Wisconsin", "timezone": "America/Chicago" },
    "609": { "state": "NJ", "city": "Central New Jersey", "timezone": "America/New_York" },
    "610": { "state": "PA", "city": "Eastern Pennsylvania", "timezone": "America/New_York" },
    "612": { "state": "MN", "city": "Minneapolis", "timezone": "America/Chicago" },
    "613": { "state": "ON", "city": "Ottawa area", "timezone": "America/Toronto" },
    "614": { "state": "OH", "city": "Columbus", "timezone": "America/New_York" },
    "615": { "state": "TN", "city": "Nashville", "timezone": "America/Chicago" },
    "616": { "state": "MI", "city": "Grand Rapids", "timezone": "America/New_York" },
    "617": { "state": "MA", "city": "Boston", "timezone": "America/New_York" },
    "618": { "state": "IL", "city": "Southern Illinois", "timezone": "America/Chicago" },
    "619": { "state": "CA", "city": "San Diego", "timezone": "America/Los_Angeles" },
    "620": { "state": "KS", "city": "Western Kansas", "timezone": "America/Chicago" },
    "623": { "state": "AZ", "city": "West Valley", "timezone": "America/Phoenix" },
    "624": { "state": "NY", "city": "New York", "timezone": "America/New_York" },
    "626": { "state": "CA", "city": "Pasadena/San Gabriel Valley", "timezone": "America/Los_Angeles" },
    "628": { "state": "CA", "city": "San Francisco area", "timezone": "America/Los_Angeles" },
    "629": { "state": "TN", "city": "Tennessee", "timezone": "America/Chicago" },
    "630": { "state": "IL", "city": "Western suburbs of Chicago", "timezone": "America/Chicago" },
    "631": { "state": "NY", "city": "Long Island (Suffolk County)", "timezone": "America/New_York" },
    "636": { "state": "MO", "city": "St. Charles County", "timezone": "America/Chicago" },
    "639": { "state": "SK", "city": "Saskatchewan", "timezone": "America/Regina" },
    "640": { "state": "NJ", "city": "New Jersey", "timezone": "America/New_York" },
    "641": { "state": "IA", "city": "North-central Iowa", "timezone": "America/Chicago" },
    "645": { "state": "FL", "city": "Florida", "timezone": "America/New_York" },
    "646": { "state": "NY", "city": "Manhattan", "timezone": "America/New_York" },
    "647": { "state": "ON", "city": "Toronto", "timezone": "America/Toronto" },
    "649": { "state": "TC", "city": "Turks and Caicos", "timezone": "America/Grand_Turk" },
    "650": { "state": "CA", "city": "Palo Alto/San Mateo", "timezone": "America/Los_Angeles" },
    "651": { "state": "MN", "city": "St. Paul", "timezone": "America/Chicago" },
    "657": { "state": "CA", "city": "Orange County", "timezone": "America/Los_Angeles" },
    "659": { "state": "AL", "city": "Central Alabama", "timezone": "America/Chicago" },
    "660": { "state": "MO", "city": "Northern Missouri", "timezone": "America/Chicago" },
    "661": { "state": "CA", "city": "Bakersfield/Lancaster", "timezone": "America/Los_Angeles" },
    "662": { "state": "MS", "city": "Northern Mississippi", "timezone": "America/Chicago" },
    "664": { "state": "MS", "city": "Montserrat", "timezone": "America/Montserrat" },
    "667": { "state": "MD", "city": "Maryland", "timezone": "America/New_York" },
    "669": { "state": "CA", "city": "San Jose", "timezone": "America/Los_Angeles" },
    "670": { "state": "MP", "city": "Northern Mariana Islands", "timezone": "Pacific/Saipan" },
    "671": { "state": "GU", "city": "Guam", "timezone": "Pacific/Guam" },
    "678": { "state": "GA", "city": "Atlanta metro", "timezone": "America/New_York" },
    "679": { "state": "MI", "city": "Michigan", "timezone": "America/New_York" },
    "680": { "state": "NY", "city": "New York", "timezone": "America/New_York" },
    "681": { "state": "WV", "city": "West Virginia", "timezone": "America/New_York" },
    "682": { "state": "TX", "city": "Fort Worth area", "timezone": "America/Chicago" },
    "683": { "state": "ON", "city": "Ontario", "timezone": "America/Toronto" },
    "684": { "state": "AS", "city": "American Samoa", "timezone": "Pacific/Pago_Pago" },
    "686": { "state": "VA", "city": "Virginia", "timezone": "America/New_York" },
    "689": { "state": "FL", "city": "Orlando area", "timezone": "America/New_York" },
    "701": { "state": "ND", "city": "All of North Dakota", "timezone": "America/Chicago" },
    "702": { "state": "NV", "city": "Las Vegas", "timezone": "America/Los_Angeles" },
    "703": { "state": "VA", "city": "Northern Virginia", "timezone": "America/New_York" },
    "704": { "state": "NC", "city": "Charlotte", "timezone": "America/New_York" },
    "705": { "state": "ON", "city": "Northern and Central Ontario", "timezone": "America/Toronto" },
    "706": { "state": "GA", "city": "Northern Georgia", "timezone": "America/New_York" },
    "707": { "state": "CA", "city": "Northern California coast", "timezone": "America/Los_Angeles" },
    "708": { "state": "IL", "city": "Chicago suburbs", "timezone": "America/Chicago" },
    "709": { "state": "NL", "city": "Newfoundland and Labrador", "timezone": "America/St_Johns" },
    "710": { "state": "US", "city": "US Government", "timezone": "America/New_York" },
    "712": { "state": "IA", "city": "Western Iowa", "timezone": "America/Chicago" },
    "713": { "state": "TX", "city": "Houston", "timezone": "America/Chicago" },
    "714": { "state": "CA", "city": "Orange County", "timezone": "America/Los_Angeles" },
    "715": { "state": "WI", "city": "Northern Wisconsin", "timezone": "America/Chicago" },
    "716": { "state": "NY", "city": "Buffalo/Niagara", "timezone": "America/New_York" },
    "717": { "state": "PA", "city": "Central Pennsylvania", "timezone": "America/New_York" },
    "718": { "state": "NY", "city": "New York City boroughs", "timezone": "America/New_York" },
    "719": { "state": "CO", "city": "Southern Colorado", "timezone": "America/Denver" },
    "720": { "state": "CO", "city": "Denver area", "timezone": "America/Denver" },
    "721": { "state": "SX", "city": "Sint Maarten", "timezone": "America/Lower_Princes" },
    "724": { "state": "PA", "city": "Western Pennsylvania", "timezone": "America/New_York" },
    "725": { "state": "NV", "city": "Southern Nevada", "timezone": "America/Los_Angeles" },
    "726": { "state": "TX", "city": "San Antonio area", "timezone": "America/Chicago" },
    "727": { "state": "FL", "city": "St. Petersburg/Clearwater", "timezone": "America/New_York" },
    "728": { "state": "FL", "city": "South Florida", "timezone": "America/New_York" },
    "729": { "state": "TN", "city": "Tennessee", "timezone": "America/New_York" },
    "730": { "state": "IL", "city": "Southern Illinois", "timezone": "America/Chicago" },
    "731": { "state": "TN", "city": "Western Tennessee", "timezone": "America/Chicago" },
    "732": { "state": "NJ", "city": "Central New Jersey", "timezone": "America/New_York" },
    "734": { "state": "MI", "city": "Ann Arbor/Monroe", "timezone": "America/New_York" },
    "737": { "state": "TX", "city": "Austin area", "timezone": "America/Chicago" },
    "738": { "state": "CA", "city": "Southern California", "timezone": "America/Los_Angeles" },
    "740": { "state": "OH", "city": "Southeastern Ohio", "timezone": "America/New_York" },
    "742": { "state": "ON", "city": "Ontario", "timezone": "America/Toronto" },
    "743": { "state": "NC", "city": "North Carolina", "timezone": "America/New_York" },
    "747": { "state": "CA", "city": "Southern California", "timezone": "America/Los_Angeles" },
    "748": { "state": "CO", "city": "Colorado", "timezone": "America/Denver" },
    "754": { "state": "FL", "city": "Broward County", "timezone": "America/New_York" },
    "757": { "state": "VA", "city": "Hampton Roads", "timezone": "America/New_York" },
    "758": { "state": "LC", "city": "Saint Lucia", "timezone": "America/St_Lucia" },
    "760": { "state": "CA", "city": "Southern California", "timezone": "America/Los_Angeles" },
    "762": { "state": "GA", "city": "Georgia", "timezone": "America/New_York" },
    "763": { "state": "MN", "city": "Northwest Metro", "timezone": "America/Chicago" },
    "764": { "state": "CA", "city": "California", "timezone": "America/Los_Angeles" },
    "765": { "state": "IN", "city": "Central Indiana", "timezone": "America/New_York" },
    "767": { "state": "DM", "city": "Dominica", "timezone": "America/Dominica" },
    "769": { "state": "MS", "city": "Mississippi", "timezone": "America/Chicago" },
    "770": { "state": "GA", "city": "Atlanta suburbs", "timezone": "America/New_York" },
    "771": { "state": "DC", "city": "Washington DC", "timezone": "America/New_York" },
    "772": { "state": "FL", "city": "Treasure Coast", "timezone": "America/New_York" },
    "773": { "state": "IL", "city": "Chicago", "timezone": "America/Chicago" },
    "774": { "state": "MA", "city": "Massachusetts", "timezone": "America/New_York" },
    "775": { "state": "NV", "city": "Northern Nevada", "timezone": "America/Los_Angeles" },
    "778": { "state": "BC", "city": "British Columbia", "timezone": "America/Vancouver" },
    "779": { "state": "IL", "city": "Northern Illinois", "timezone": "America/Chicago" },
    "780": { "state": "AB", "city": "Northern Alberta", "timezone": "America/Edmonton" },
    "781": { "state": "MA", "city": "Eastern Massachusetts", "timezone": "America/New_York" },
    "782": { "state": "NS", "city": "Nova Scotia", "timezone": "America/Halifax" },
    "784": { "state": "VC", "city": "Saint Vincent/Grenadines", "timezone": "America/St_Vincent" },
    "785": { "state": "KS", "city": "Northern Kansas", "timezone": "America/Chicago" },
    "786": { "state": "FL", "city": "South Florida", "timezone": "America/New_York" },
    "787": { "state": "PR", "city": "Puerto Rico", "timezone": "America/Puerto_Rico" },
    "801": { "state": "UT", "city": "Northern Utah", "timezone": "America/Denver" },
    "802": { "state": "VT", "city": "All of Vermont", "timezone": "America/New_York" },
    "803": { "state": "SC", "city": "Central South Carolina", "timezone": "America/New_York" },
    "804": { "state": "VA", "city": "Richmond/Petersburg", "timezone": "America/New_York" },
    "805": { "state": "CA", "city": "Central Coast", "timezone": "America/Los_Angeles" },
    "806": { "state": "TX", "city": "Texas Panhandle", "timezone": "America/Chicago" },
    "807": { "state": "ON", "city": "Northwestern Ontario", "timezone": "America/Winnipeg" },
    "808": { "state": "HI", "city": "Hawaii", "timezone": "Pacific/Honolulu" },
    "809": { "state": "DO", "city": "Dominican Republic", "timezone": "America/Santo_Domingo" },
    "810": { "state": "MI", "city": "Flint/Port Huron", "timezone": "America/New_York" },
    "812": { "state": "IN", "city": "Southern Indiana", "timezone": "America/New_York" },
    "813": { "state": "FL", "city": "Tampa", "timezone": "America/New_York" },
    "814": { "state": "PA", "city": "Central Pennsylvania", "timezone": "America/New_York" },
    "815": { "state": "IL", "city": "Northern Illinois", "timezone": "America/Chicago" },
    "816": { "state": "MO", "city": "Kansas City", "timezone": "America/Chicago" },
    "817": { "state": "TX", "city": "Fort Worth/Arlington", "timezone": "America/Chicago" },
    "818": { "state": "CA", "city": "San Fernando Valley", "timezone": "America/Los_Angeles" },
    "819": { "state": "QC", "city": "Western Quebec", "timezone": "America/Montreal" },
    "820": { "state": "CA", "city": "Central Coast", "timezone": "America/Los_Angeles" },
    "821": { "state": "SC", "city": "South Carolina", "timezone": "America/New_York" },
    "825": { "state": "AB", "city": "Southern Alberta", "timezone": "America/Edmonton" },
    "826": { "state": "VA", "city": "Virginia", "timezone": "America/New_York" },
    "828": { "state": "NC", "city": "Western North Carolina", "timezone": "America/New_York" },
    "829": { "state": "DO", "city": "Dominican Republic", "timezone": "America/Santo_Domingo" },
    "830": { "state": "TX", "city": "South Texas", "timezone": "America/Chicago" },
    "831": { "state": "CA", "city": "Monterey Bay", "timezone": "America/Los_Angeles" },
    "832": { "state": "TX", "city": "Houston", "timezone": "America/Chicago" },
    "835": { "state": "PA", "city": "Pennsylvania", "timezone": "America/New_York" },
    "836": { "state": "MN", "city": "Minnesota", "timezone": "America/Chicago" },
    "837": { "state": "CA", "city": "California", "timezone": "America/Los_Angeles" },
    "838": { "state": "NY", "city": "New York", "timezone": "America/New_York" },
    "839": { "state": "SC", "city": "South Carolina", "timezone": "America/New_York" },
    "840": { "state": "CA", "city": "California", "timezone": "America/Los_Angeles" },
    "843": { "state": "SC", "city": "South Carolina", "timezone": "America/New_York" },
    "845": { "state": "NY", "city": "Hudson Valley", "timezone": "America/New_York" },
    "847": { "state": "IL", "city": "Chicago suburbs", "timezone": "America/Chicago" },
    "848": { "state": "NJ", "city": "New Jersey", "timezone": "America/New_York" },
    "849": { "state": "DO", "city": "Dominican Republic", "timezone": "America/Santo_Domingo" },
    "850": { "state": "FL", "city": "Florida panhandle", "timezone": "America/Chicago" },
    "854": { "state": "SC", "city": "South Carolina", "timezone": "America/New_York" },
    "856": { "state": "NJ", "city": "Southern New Jersey", "timezone": "America/New_York" },
    "857": { "state": "MA", "city": "Boston area", "timezone": "America/New_York" },
    "858": { "state": "CA", "city": "San Diego", "timezone": "America/Los_Angeles" },
    "859": { "state": "KY", "city": "Central Kentucky", "timezone": "America/New_York" },
    "860": { "state": "CT", "city": "Connecticut", "timezone": "America/New_York" },
    "861": { "state": "IL", "city": "Illinois", "timezone": "America/Chicago" },
    "862": { "state": "NJ", "city": "Northern New Jersey", "timezone": "America/New_York" },
    "863": { "state": "FL", "city": "Central Florida", "timezone": "America/New_York" },
    "864": { "state": "SC", "city": "Upstate South Carolina", "timezone": "America/New_York" },
    "865": { "state": "TN", "city": "Knoxville", "timezone": "America/New_York" },
    "867": { "state": "YT", "city": "Yukon/NWT/Nunavut", "timezone": "America/Whitehorse" },
    "868": { "state": "TT", "city": "Trinidad and Tobago", "timezone": "America/Port_of_Spain" },
    "869": { "state": "KN", "city": "Saint Kitts and Nevis", "timezone": "America/St_Kitts" },
    "870": { "state": "AR", "city": "Arkansas", "timezone": "America/Chicago" },
    "872": { "state": "IL", "city": "Chicago", "timezone": "America/Chicago" },
    "873": { "state": "QC", "city": "Quebec", "timezone": "America/Montreal" },
    "876": { "state": "JM", "city": "Jamaica", "timezone": "America/Jamaica" },
    "878": { "state": "PA", "city": "Pittsburgh area", "timezone": "America/New_York" },
    "901": { "state": "TN", "city": "Memphis", "timezone": "America/Chicago" },
    "902": { "state": "NS", "city": "Nova Scotia/PEI", "timezone": "America/Halifax" },
    "903": { "state": "TX", "city": "Eastern Texas", "timezone": "America/Chicago" },
    "904": { "state": "FL", "city": "Jacksonville", "timezone": "America/New_York" },
    "905": { "state": "ON", "city": "Southern Ontario", "timezone": "America/Toronto" },
    "906": { "state": "MI", "city": "Upper Peninsula", "timezone": "America/New_York" },
    "907": { "state": "AK", "city": "Alaska", "timezone": "America/Anchorage" },
    "908": { "state": "NJ", "city": "North-central NJ", "timezone": "America/New_York" },
    "909": { "state": "CA", "city": "Inland Empire", "timezone": "America/Los_Angeles" },
    "910": { "state": "NC", "city": "Southeastern NC", "timezone": "America/New_York" },
    "912": { "state": "GA", "city": "Southeastern GA", "timezone": "America/New_York" },
    "913": { "state": "KS", "city": "Kansas City area", "timezone": "America/Chicago" },
    "914": { "state": "NY", "city": "Westchester County", "timezone": "America/New_York" },
    "915": { "state": "TX", "city": "El Paso", "timezone": "America/Denver" },
    "916": { "state": "CA", "city": "Sacramento", "timezone": "America/Los_Angeles" },
    "917": { "state": "NY", "city": "New York City", "timezone": "America/New_York" },
    "918": { "state": "OK", "city": "Tulsa", "timezone": "America/Chicago" },
    "919": { "state": "NC", "city": "Raleigh/Durham", "timezone": "America/New_York" },
    "920": { "state": "WI", "city": "Eastern Wisconsin", "timezone": "America/Chicago" },
    "925": { "state": "CA", "city": "East Bay", "timezone": "America/Los_Angeles" },
    "928": { "state": "AZ", "city": "Northern Arizona", "timezone": "America/Phoenix" },
    "929": { "state": "NY", "city": "New York City", "timezone": "America/New_York" },
    "930": { "state": "IN", "city": "Southern Indiana", "timezone": "America/New_York" },
    "931": { "state": "TN", "city": "Middle Tennessee", "timezone": "America/Chicago" },
    "934": { "state": "NY", "city": "Long Island", "timezone": "America/New_York" },
    "936": { "state": "TX", "city": "Eastern Texas", "timezone": "America/Chicago" },
    "937": { "state": "OH", "city": "Dayton/Springfield", "timezone": "America/New_York" },
    "938": { "state": "AL", "city": "Northern Alabama", "timezone": "America/Chicago" },
    "939": { "state": "PR", "city": "Puerto Rico", "timezone": "America/Puerto_Rico" },
    "940": { "state": "TX", "city": "North Texas", "timezone": "America/Chicago" },
    "941": { "state": "FL", "city": "Sarasota/Manatee", "timezone": "America/New_York" },
    "942": { "state": "ON", "city": "Ontario", "timezone": "America/Toronto" },
    "945": { "state": "TX", "city": "Dallas area", "timezone": "America/Chicago" },
    "947": { "state": "MI", "city": "Oakland County", "timezone": "America/New_York" },
    "948": { "state": "VA", "city": "Virginia", "timezone": "America/New_York" },
    "949": { "state": "CA", "city": "Orange County", "timezone": "America/Los_Angeles" },
    "951": { "state": "CA", "city": "Riverside County", "timezone": "America/Los_Angeles" },
    "952": { "state": "MN", "city": "Southwest Metro", "timezone": "America/Chicago" },
    "954": { "state": "FL", "city": "Broward County", "timezone": "America/New_York" },
    "956": { "state": "TX", "city": "Rio Grande Valley", "timezone": "America/Chicago" },
    "959": { "state": "CT", "city": "Connecticut", "timezone": "America/New_York" },
    "970": { "state": "CO", "city": "Western Colorado", "timezone": "America/Denver" },
    "971": { "state": "OR", "city": "Oregon", "timezone": "America/Los_Angeles" },
    "972": { "state": "TX", "city": "Dallas area", "timezone": "America/Chicago" },
    "973": { "state": "NJ", "city": "Northern New Jersey", "timezone": "America/New_York" },
    "975": { "state": "MO", "city": "Missouri", "timezone": "America/Chicago" },
    "978": { "state": "MA", "city": "Northern MA", "timezone": "America/New_York" },
    "979": { "state": "TX", "city": "Brazos Valley", "timezone": "America/Chicago" },
    "980": { "state": "NC", "city": "Charlotte area", "timezone": "America/New_York" },
    "983": { "state": "CO", "city": "Colorado", "timezone": "America/Denver" },
    "984": { "state": "NC", "city": "North Carolina", "timezone": "America/New_York" },
    "985": { "state": "LA", "city": "Southeastern LA", "timezone": "America/Chicago" },
    "986": { "state": "ID", "city": "Idaho", "timezone": "America/Denver" },
    "989": { "state": "MI", "city": "Central Michigan", "timezone": "America/New_York" }
}

export function getPhoneLocation(phone) {
    const digits = phone.replace(/\D/g, "");
    const areaCode = digits.length === 11 ? digits.slice(1, 4) : digits.slice(0, 3);
    return areaCodeInfo[areaCode] || null;
}

export const Shades = {
    sky: [
        "oklch(90.1% 0.058 230.902)",
        "oklch(82.8% 0.111 230.318)",
        "oklch(74.6% 0.16 232.661)",
        "oklch(68.5% 0.169 237.323)",
        "oklch(58.8% 0.158 241.966)",
        "oklch(50% 0.134 242.749)",
        "oklch(44.3% 0.11 240.79)",
        "oklch(39.1% 0.09 240.876)",
        "oklch(29.3% 0.066 243.157)",
    ],
    emerald: [
        "oklch(90.5% 0.093 164.15)",
        "oklch(84.5% 0.143 164.978)",
        "oklch(76.5% 0.177 163.223)",
        "oklch(69.6% 0.17 162.48)",
        "oklch(59.6% 0.145 163.225)",
        "oklch(50.8% 0.118 165.612)",
        "oklch(43.2% 0.095 166.913)",
        "oklch(37.8% 0.077 168.94)",
        "oklch(26.2% 0.051 172.552)",
    ]

};
